// v2.4 US-2.2: a tiny event bus so the API client can tell the layout that a
// response came from the service worker's cache ("showing saved data").
//
// #112: the banner is tracked per request path. Before, ANY fresh response
// (the venues list, the unread badge poll) cleared it, so a schedule served
// stale from cache after a save showed the banner for a split second at
// most. Now a path's banner clears only when that same path answers fresh,
// when the page changes (resetCached), or on a fresh answer with no path.
const listeners = new Set();
const cachedByPath = new Map(); // path -> cachedAt
let lastCachedAt = null;

const publish = () => {
  // the oldest saved copy on screen is the one worth mentioning
  const next = cachedByPath.size ? [...cachedByPath.values()].sort()[0] : null;
  if (next === lastCachedAt) return;
  lastCachedAt = next;
  listeners.forEach((fn) => fn(lastCachedAt));
};

export const noteCachedResponse = (cachedAt, path = '*') => {
  cachedByPath.set(path, cachedAt || new Date().toISOString());
  publish();
};
export const noteFreshResponse = (path) => {
  if (path === undefined) cachedByPath.clear(); else cachedByPath.delete(path);
  publish();
};
export const resetCached = () => { cachedByPath.clear(); publish(); };
export const cachedPaths = () => [...cachedByPath.keys()].filter((p) => p !== '*');

// H-09: back online, pages that showed saved data reload it (the fresh answers
// clear the banner). api.js fires this on the browser's 'online' event.
const reconnectListeners = new Set();
export const subscribeReconnect = (fn) => { reconnectListeners.add(fn); return () => reconnectListeners.delete(fn); };
export const notifyReconnect = () => { reconnectListeners.forEach((fn) => { try { fn(); } catch (e) { /* a page's reload failed; the fallback below covers the banner */ } }); };
export const subscribeCached = (fn) => { listeners.add(fn); fn(lastCachedAt); return () => listeners.delete(fn); };

// Friendly text for a failed request; offline first, then the server's own
// message, then the caller's fallback.
export const errorMessage = (err, fallback = 'Something went wrong. Please try again.') => {
  if (err?.offline || err?.response?.data?.code === 'offline') return "You're offline. Reconnect and try again — nothing was saved.";
  return err?.response?.data?.error || fallback;
};

// X-20: sign-out wipes the service worker's cached API reads so a shared
// device does not keep the last family's schedule and messages. Best effort:
// no service worker (or no support) means there is nothing cached.
export const clearOfflineCache = () => {
  try {
    const sw = typeof navigator !== 'undefined' ? navigator.serviceWorker : null;
    if (!sw) return;
    if (sw.controller) { sw.controller.postMessage({ type: 'CLEAR' }); return; }
    if (typeof sw.getRegistration === 'function') {
      Promise.resolve(sw.getRegistration('/sw.js')).then((reg) => reg?.active?.postMessage({ type: 'CLEAR' })).catch(() => {});
    }
  } catch (e) { /* nothing to clear */ }
};
